<template>
  <div
    class="nwx-picklist"
    :class="[{
      'open': open,
      'disabled': disabled,
      'filterable': filterable,
      'clearable': clearable,
      'clear': valueIsEmpty,
      'multiple': multiple,
      'dense': dense,
      'no-border': noBorder,
      'ellipsis': ellipsis,
    },
    themeClass,
    customClass
    ]"
    ref="select"
    v-click-outside="closeDropdown"
  >
    <input
      type="hidden"
      readonly="true"
      autocomplete="off"
      :name="name"
      :value="value"
    >
    <div class="nwx-select" @click="clickDropdown">
      <div class="nwx-selected">
        <div class="nwx-label">
          <template v-if="values">
            <template v-if="multiple && chips">
              <span class="chip" v-for="value in values" :key="value.value">
                  {{ value.text }}
                  <span class="remove" @click.stop="removeValue(value)">&times;</span>
              </span>
            </template>
            <template v-else>
              <span class="value">{{ values }}</span>
            </template>
          </template>
          <template v-else>
            <span class="nwx-placeholder">{{ placeholder }}</span>
          </template>
        </div>
        <div class="nwx-control" @click="actionControl"></div>
      </div>
    </div>
    <div class="nwx-content-wrapper">
      <div class="nwx-content">
        <div class="filtrable" v-if="filterable">
          <input
            v-model="filter"
            type="text"
            ref="filter"
            placeholder="Search"
          >
        </div>
        <div class="nwx-list">
          <ul v-if="filteredOptions.length > 0">
            <li
              v-for="option in filteredOptions"
              :key="option.value"
              :class="{
                'disabled': option.disabled,
                'selected': isSelectedOption(option.value)
              }"
              @click="selectOption(option)"
            >
              <slot :option="option">
                <div class="option-default">
                  <span class="option-label">{{ option.text }}</span>
                  <span class="option-icon"></span>
                </div>
              </slot>
            </li>
          </ul>
          <div class="no-match" v-else>No matching data</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import ClickOutside from 'vue-click-outside';

  export default {
    name: 'NwxSelect',
    directives: {
      ClickOutside,
    },
    // emits: ['update:value'],
    props: {
      theme: {
        type: String,
        default: 'classic',
        validator: (themeVersion) => (['classic', 'pp-modern'].includes(themeVersion)),
      },
      name: {
        type: String,
        required: true,
      },
      placeholder: {
        type: String,
        default: 'No selected',
      },
      items: {
        type: Array,
        required: true,
      },
      value: {
        type: [String, Number, Array],
        default: '',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      filterable: {
        type: Boolean,
        default: false,
      },
      clearable: {
        type: Boolean,
        default: false,
      },
      multiple: {
        type: Boolean,
        default: false,
      },
      chips: {
        type: Boolean,
        default: true,
      },
      dense: {
        type: Boolean,
        default: false,
      },
      noBorder: {
        type: Boolean,
        default: false,
      },
      limit: {
        type: [Number, String],
        default: 0,
      },
      customClass: {
        type: String,
        default: '',
      },
      ellipsis: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        open: false,
        filter: '',
        itemsMultiple: [],
        width: '100%',
      };
    },
    methods: {
      clickDropdown() {
        if (!this.disabled) {
          if (this.open) {
            this.closeDropdown();
          } else {
            this.openDropdown();
          }
        }
      },
      openDropdown() {
        this.open = true;

        this.$nextTick(() => {
          this.focusFilter();
        });
      },
      closeDropdown() {
        this.open = false;
        this.clearFilter();
      },
      focusFilter() {
        if (this.filterable) {
          this.$refs.filter.focus();
        }
      },
      clearFilter() {
        this.filter = '';
      },
      updateModel(value) {
        this.$emit('input', value);
      },
      removeValue(option) {
        this.itemsMultiple = this.itemsMultiple.map((item) => ((item.value === option.value)
          ? { ...item, selected: false }
          : item));

        const options = this.itemsMultiple
          .filter((item) => item.selected)
          .map((item) => item.value);

        this.updateModel(options);
      },
      selectOption(option) {
        if (!option.disabled) {
          let options;

          if (this.multiple) {
            this.itemsMultiple = this.itemsMultiple.map((item) => ((item.value === option.value)
              ? { ...item, selected: !item.selected }
              : item));

            options = this.itemsMultiple
              .filter((item) => item.selected)
              .map((item) => item.value);

            this.clearFilter();
            this.focusFilter();
          } else {
            options = option.value;
          }

          this.updateModel(options);

          if (!this.multiple) {
            this.closeDropdown();
          }
        }
      },
      isSelectedOption(value) {
        if (this.multiple && Array.isArray(this.value)) {
          return this.value.includes(value);
        }
        return this.value === value;
      },
      actionControl(event) {
        if (this.clearable) {
          if (!this.valueIsEmpty) {
            event.stopPropagation();
          }
          const model = this.multiple ? [] : '';
          this.updateModel(model);
          this.clearSelectedMultipleItems();
          this.closeDropdown();
          this.$emit('clear');
        }
      },
      clearSelectedMultipleItems() {
        if (this.multiple && Array.isArray(this.value)) {
          this.itemsMultiple = this.items.map((item) => ({
            value: item.value,
            selected: false,
          }));
        }
      },
      updateSelectedMultipleItems() {
        if (this.multiple && Array.isArray(this.value)) {
          this.itemsMultiple = this.items.map((item) => ({
            value: item.value,
            selected: this.value.includes(item.value),
          }));
        }
      },
    },
    updated() {
      this.updateSelectedMultipleItems();
    },
    computed: {
      safeTheme() { // guarantee a safe value
        const allowed = ['classic', 'pp-modern'];
        return allowed.includes(this.theme) ? this.theme : 'classic';
      },
      themeClass() {
        return `theme--${this.safeTheme}`;
      },
      values() {
        if (this.multiple && Array.isArray(this.value)) {
          const options = this.items
            .filter((item) => this.value.includes(item.value));

          if (options.length > 0) {
            if (this.chips) {
              return options;
            }
            return options.map((item) => item.text).join('; ');
          }
          return null;
        }
        const option = this.items.find((item) => item.value === this.value);
        return option ? option.text : null;
      },
      filteredOptions() {
        let options;

        if (this.filter) {
          options = this.items
            .filter((item) => item.text.toLowerCase()
              .indexOf(this.filter.toLowerCase()) >= 0);
        } else {
          options = this.items;
        }

        if (
          this.multiple
          && this.limit
          && Array.isArray(this.values)
          && this.values.length >= this.limit
        ) {
          return options.map((item) => {
            if (!this.value.includes(item.value)) {
              return { ...item, disabled: true };
            }

            return item;
          });
        }

        return options;
      },
      valueIsEmpty() {
        if (this.multiple) {
          return this.value.length === 0;
        }
        return this.value === '';
      },
    },
  };
</script>

<style lang="less" scoped>

@import '../assets/variables';

div, input {
  box-sizing: border-box;
}
// classic theme
.nwx-picklist {
  position: relative;
  font-family: "Open Sans", sans-serif;
  font-weight: 500;
  font-size: 14px;
  max-width: 100%;

  &.open {
    .nwx-select {
      border-color: #0068da;
      border-bottom-color: #C4CAD0;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;

      .nwx-selected {
        .nwx-control {
          &:before {
            transform: rotate(-90deg);
          }
        }
      }
    }

    .nwx-content-wrapper {
      .nwx-content {
        display: block;
      }
    }
  }

  &.disabled {
    .nwx-select {
      cursor: not-allowed;
      color: fade(@mainGray, 80%);
      border-color: fade(@mainGray, 20%) !important;
      background-color: fade(@bodyWhite, 60%) !important;

      .nwx-label {
        .nwx-placeholder{
          color: fade(@mainGray, 80%) !important;
        }
      }
    }
  }

  .clearControl() {
    content: '\00d7';
    display: inline-block;
    width: 14px;
    height: 14px;
    text-align: center;
    transition: none;
    position: absolute;
    transform: rotate(0);
    left: 2px;
    top: -9px;
    font-size: 22px;
    color: #ccc;
    border-radius: 50%;
  }

  &.clearable:not(.clear) {
    .nwx-select:hover {
      .nwx-selected {
        .nwx-control {

          &:hover:before {
            border-color: #888;
            color: #888;
          }

          &:before {
            .clearControl()
          }
        }
      }
    }
  }

  &.clearable:not(.clear).open {
    .nwx-select {
      .nwx-selected {
        .nwx-control {
          &:before {
            .clearControl()
          }
        }
      }
    }
  }

  &.multiple {
    .nwx-select .nwx-selected .nwx-label {
      padding: 7px;
    }
  }

  &.dense {
    font-size: 12px;

    .nwx-select .nwx-selected {
      min-height: 28px;

      .nwx-label {
        padding: 2px 3px;

        .cheap {
          padding: 1px 3px;

          .remove {
            margin-left: 2px;
          }
        }
      }
    }

    .nwx-content-wrapper .nwx-content {
      ul li {
        padding: 5px 7px;
      }

      .filtrable {
        padding: 7px;

        input {
          padding: 3px 5px;
        }
      }
    }
  }

  &.no-border {
    .nwx-select {
      border-color: transparent;
    }

    .nwx-content-wrapper {
      .nwx-content {
        border-color: transparent;
      }
    }
  }

  &.ellipsis {
    .nwx-selected .nwx-label .value {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .nwx-select {
    background-color: #fff;
    border-radius: 5px;
    border: 1px solid #C4CAD0;
    position: relative;
    cursor: pointer;

    .nwx-selected {
      min-height: 40px;
      display: flex;
      justify-content: space-around;
      align-items: center;

      .nwx-label {
        flex-grow: 1;
        padding: 7px 10px;
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
        width: calc(100% - 40px);

        .chip {
          border: 1px solid #ddd;
          border-radius: 3px;
          padding: 2px 5px;
          background-color: #eee;
          position: relative;
          cursor: text;

          .remove {
            margin-left: 5px;
            cursor: pointer;
          }
        }

        .value {
          padding: 2px 5px;
        }

        .nwx-placeholder {
          padding: 2px 5px;
          color: #888;
        }
      }

      .nwx-control {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        position: relative;
        width: 20px;
        height: 14px;
        margin: 0 10px;

        &:before {
          content: '\276F';
          left: 1px;
          transform: rotate(90deg);
          transition: transform .3s;
        }
      }
    }
  }

  .nwx-content-wrapper {
    height: 0px;
    position: relative;

    .nwx-content {
      background-color: #fff;
      box-shadow: 1px 1px 3px #ddd;
      border-radius: 0 0 5px 5px;
      border: 1px solid #0068da;
      border-top-color: #fff;
      position: absolute;
      z-index: 10;
      top: 0px;
      left: 0px;
      right: 0;
      display: none;

      .filtrable {
        padding: 10px;

        input {
          width: 100%;
          font-family: inherit;
          background-color: #fff;
          border: 1px solid #ddd;
          border-radius: 5px;
          outline: none;
          padding: 9px 15px;
          height: auto;

          &::placeholder {
            color: #bbb;
          }

          &:focus {
            box-shadow: none;
          }
        }
      }

      .nwx-list {
        padding-right: 1px;
        padding-bottom: 1px;
      }

      ul {
        max-height: 175px;
        overflow-y: auto;
        list-style: none;
        padding-left: 0;
        margin: 0;

        &::-webkit-scrollbar {
          width: 6px;
        }

        &::-webkit-scrollbar-track {
          background: #fff;
        }

        &::-webkit-scrollbar-thumb {
          background: #bdbdbd;
          border-radius: 3px;
        }

        li {
          padding: 10px 15px;
          cursor: pointer;

          &.disabled {
            cursor: not-allowed;
            color: #aaa;
          }

          &.selected {
            .option-default {
              color: #0068da;

              .option-icon {
                &:before {
                  content: '';
                  display: inline-block;
                  position: absolute;
                  width: 6px;
                  top: 5px;
                  right: 8px;
                  border-bottom: 1px solid #0068da;
                  transform: rotate(45deg);
                }

                &:after {
                  content: '';
                  display: inline-block;
                  position: absolute;
                  width: 11px;
                  top: 4px;
                  right: 0px;
                  border-bottom: 1px solid #0068da;
                  transform: rotate(-45deg);
                }
              }
            }
          }

          &:hover {
            &:not(.disabled) {
              color: @vigilantBlue;
            }
          }

          &:last-child {
            border: 0 0 5px 5px;
          }

          .option-default {
            display: flex;
            justify-content: space-between;
            align-items: center;

            .option-icon {
              width: 15px;
              height: 10px;
              position: relative;
            }
          }
        }
      }

      .no-match {
        padding: 10px 15px;
        color: #aaa;
      }
    }
  }
}

// pp-modern theme
.nwx-picklist.theme--pp-modern {
  font-size: 1rem;
  font-family: Syne, Syne-fallback, Trebuchet MS, Verdana, sans-serif;

  .nwx-select {
    border-radius: 0;
    background-color: @accessWhite;
    .nwx-selected{
      min-height: 38px;
      .nwx-label{
        .nwx-placeholder{
          color: #000;
        }
        .value{
          padding: 0 5px;
        }
      }
      .nwx-control {
        &:before {
          content: '';
          background-image: none;
          width: .75rem;
          height: .5rem;
          mask: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11.7147 1.8593L6.00042 7.67969L0.286133 1.8593L1.28613 0.822545L6.00042 5.49704L10.7147 0.822545L11.7147 1.8593Z' fill='%23ffffff'/%3E%3C/svg%3E");
          mask-size: contain;
          mask-repeat: no-repeat;
          mask-position: center;
          pointer-events: none;
          background-color: @vigilantBlue;
          transform: rotate(0);
          transition: transform .3s cubic-bezier(.2,0,.38,.9);
        }
      }
    }
  }
  &:not(.clear) .nwx-select{
    border-color: rgba(77, 77, 77, .3);
  }
  &.clearable:not(.clear) .nwx-select:hover .nwx-selected .nwx-control:before,
  &.clearable:not(.clear).open .nwx-select .nwx-selected .nwx-control:before{
    mask:none;
    background: inherit;
    &:hover{
      color: #000000;
    }
  }
  &.open .nwx-select {
    border-top-color: @vigilantBlue;
    border-left-color: @vigilantBlue;
    border-right-color: @vigilantBlue;
    .nwx-selected .nwx-control {
        &:before {
          transform: rotate(180deg);
        }
    }
  }

  &:not(.open) .nwx-select {
    &:hover{
      border-color: #000000;
    }
    &:not(.clear):hover{
      border-color: #000000;
    }
  }

  .nwx-content-wrapper{
    .nwx-content{
      border-left-color: @vigilantBlue;
      border-right-color: @vigilantBlue;
      border-bottom-color: @vigilantBlue;
      border-radius: 0;
      background-color: @accessWhite;
      border-top: none;
      box-shadow: none;
      top: -1px;

      .filtrable {
        input {
          background-color: @accessWhite;
          border-color: @colorH100;
          color: @colorH100;
          &::placeholder {
            color: @colorH100;
          }
        }
      }

      .nwx-list {
        border-radius: 0;
      }

      ul li.selected .option-default {
        color: @vigilantBlue;
        .option-icon {
          &:after {
            border-bottom-color: @vigilantBlue;
          }
          &:before {
            border-bottom-color: @vigilantBlue;
          }
        }
      }
    }
  }
}
</style>
